import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that handles cart upsell product addition.
 *
 * @extends {Component}
 */
class CartUpsellComponent extends Component {
  /** @type {boolean} */
  #isAdding = false;

  connectedCallback() {
    super.connectedCallback();

    const addButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-cart-upsell-add]'));
    if (addButton && !addButton.dataset.listenerAttached) {
      addButton.addEventListener('click', this.#handleAddToCart.bind(this));
      addButton.dataset.listenerAttached = 'true';
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Handles adding the upsell product to cart.
   * @param {MouseEvent} event - The click event
   */
  async #handleAddToCart(event) {
    // Prevent double submissions
    if (this.#isAdding) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const button = /** @type {HTMLButtonElement} */ (event.currentTarget);
    
    // Check if button is already disabled (prevent double-clicks)
    if (button.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const upsellContainer = /** @type {HTMLElement | null} */ (this.closest('.cart-upsell'));
    
    if (!upsellContainer) return;

    const productId = upsellContainer.dataset.upsellProductId;
    const variantId = upsellContainer.dataset.upsellVariantId;

    if (!productId || !variantId) {
      console.error('Cart upsell: Missing product or variant ID');
      return;
    }

    // Set flag to prevent double submissions
    this.#isAdding = true;

    // Disable button and show loading state
    button.disabled = true;
    const loadingText = button.querySelector('.cart-upsell__add-button-loading');
    const buttonText = button.querySelector('.cart-upsell__add-button-text');
    if (loadingText) loadingText.classList.remove('hidden');
    if (buttonText) buttonText.classList.add('hidden');

    // Store references for error handling
    const resetButton = () => {
      button.disabled = false;
      if (loadingText) loadingText.classList.add('hidden');
      if (buttonText) buttonText.classList.remove('hidden');
    };

    // Get all cart-items-component section IDs for updating
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    // Prepare form data
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length > 0) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        // Error adding to cart
        console.error('Cart upsell: Error adding product', result.message);
        this.#isAdding = false;
        resetButton();
        return;
      }

      // Success - dispatch cart update event
      const cartItemsComponent = /** @type {HTMLElement | null} */ (document.querySelector('cart-items-component'));
      const sectionId = cartItemsComponent?.dataset.sectionId;

      if (sectionId && result.sections) {
        // Dispatch cart add event (which triggers cart update in cart-items-component)
        // The cart-items-component will handle morphing the section smoothly
        const formId = cartItemsComponent.id || sectionId;
        document.dispatchEvent(
          new CartAddEvent({}, formId, {
            source: 'cart-upsell-component',
            itemCount: 1,
            productId: productId,
            sections: result.sections,
          })
        );
      } else {
        // Fallback: refresh the section if event system didn't work
        if (sectionId) {
          sectionRenderer.renderSection(sectionId, { cache: false });
        }
      }

      // Hide the upsell since product is now in cart
      if (upsellContainer instanceof HTMLElement) {
        upsellContainer.style.display = 'none';

        const slider = upsellContainer.closest('[data-cart-upsell-slider]');
        if (slider instanceof HTMLElement) {
          const hasVisibleUpsells = Array.from(slider.querySelectorAll('.cart-upsell')).some(
            (item) => item instanceof HTMLElement && item.style.display !== 'none'
          );

          if (!hasVisibleUpsells) {
            slider.hidden = true;
          }
        }
      }

      // Reset flag after successful add
      this.#isAdding = false;

    } catch (error) {
      console.error('Cart upsell: Error adding product to cart', error);
      this.#isAdding = false;
      resetButton();
    }
  }

  /**
   * Handles cart update events to hide upsell if product is already in cart.
   * @param {Event} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    const upsellContainer = /** @type {HTMLElement | null} */ (this.closest('.cart-upsell'));
    if (!upsellContainer) return;

    const productId = upsellContainer.dataset.upsellProductId;
    if (!productId) return;

    // Check if the upsell product is now in the cart
    // This is a simple check - in a real scenario, you might want to check the cart items
    // For now, we'll rely on the server-side check in the Liquid template
  };
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}

// Auto-initialize upsell components that might be dynamically added
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const upsells = document.querySelectorAll('.cart-upsell:not(:has(cart-upsell-component))');
    upsells.forEach((upsell) => {
      const button = upsell.querySelector('[data-cart-upsell-add]');
      if (button && !button.closest('cart-upsell-component') && button.parentNode) {
        const component = document.createElement('cart-upsell-component');
        button.parentNode.insertBefore(component, button);
        component.appendChild(button);
      }
    });
  });
} else {
  // DOM already loaded, check immediately
  const upsells = document.querySelectorAll('.cart-upsell:not(:has(cart-upsell-component))');
  upsells.forEach((upsell) => {
    const button = upsell.querySelector('[data-cart-upsell-add]');
    if (button && !button.closest('cart-upsell-component') && button.parentNode) {
      const component = document.createElement('cart-upsell-component');
      button.parentNode.insertBefore(component, button);
      component.appendChild(button);
    }
  });
}

